import { NextPage } from 'next';
import Head from 'next/head';
import { useTranslation } from 'next-i18next';
import Layout from 'src/common/components/Layout';
import Header from 'src/common/components/Header';
import handleApiErrors from 'src/common/utils/handleApiErrors';
import { isHashedId } from 'src/common/utils/validators';
import serverGetUser from 'src/common/utils/serverGetUser';
import withCommonMiddleware from 'src/common/utils/withCommonMiddleware';
import serverGetInquiry from 'src/common/utils/serverGetInquiry';
import serverGetBroadcastBoats from 'src/common/utils/serverGetBroadcastBoats';
import ensureString from 'src/common/utils/ensureString';
import userHasSession from 'src/common/utils/userHasSession';
import { redirectInvalidSession, redirectInvalidUser } from 'src/common/utils/sessionValidationRedirects';
import serverGetListing, { isListingRedirect } from 'src/common/utils/serverGetListing';
import prependLocale from 'src/common/utils/prependLocale';
import { FullBoatDetail } from 'src/common/types/api/v4/boats';
import { FullBoatSearchDetail } from 'src/common/types/api/v4/boats/search';
import { loadTranslations } from 'src/common/utils/serverSidePropsHelpers';
import { InquiryBroadcastError } from 'src/inquiry/components/InquiryBroadcastErrors';
import InquiryBroadcastPage from 'src/inquiry/components/InquiryBroadcastPage';

type ErrorProps = {
  type: 'error';
  error: InquiryBroadcastError;
  inquiryId: string;
};

type SuccessProps = {
  type: 'success';
  broadcastBoats: FullBoatDetail[];
  inquiryId: string;
  tripId: number;
  listing: FullBoatSearchDetail;
};

export type InquiryBroadcastPageServerProps = ErrorProps | SuccessProps;

/** Href from `renter-no-offer-2-hr` email */
const BroadcastPage: NextPage<InquiryBroadcastPageServerProps> = (props) => {
  const [t] = useTranslation('inquiry-broadcast');
  return (
    <>
      <Head>
        <title>{t('title')}</title>
      </Head>
      <Layout>
        <Header withNavigation={false} />
        <InquiryBroadcastPage props={props} />
      </Layout>
    </>
  );
};

type Params = {
  listing_id: string;
};
type Query = {
  inquiryId?: string;
};

export const getServerSideProps = handleApiErrors(
  withCommonMiddleware<InquiryBroadcastPageServerProps, Params, Query>(
    async (context) => {
      const inquiryId = ensureString(context.query.inquiryId);
      if (!isHashedId(context.params?.listing_id) || !inquiryId.length) {
        return { notFound: true };
      }

      if (!userHasSession(context)) {
        return redirectInvalidSession(context);
      }

      const translations = loadTranslations(
        context.locale,
        ['common', 'navbar', 'inquiry-success', 'inquiry-broadcast'],
      );

      const [user, inquiry, listing] = await Promise.all([
        serverGetUser(context),
        serverGetInquiry(context, inquiryId),
        serverGetListing(context),
      ]);

      if (!inquiry) {
        return { notFound: true };
      }

      if (isListingRedirect(listing)) {
        return {
          redirect: {
            statusCode: listing.code,
            destination: prependLocale(listing.redirect_url, context.locale),
          },
        };
      }

      if (!user.id) {
        return redirectInvalidUser(context);
      }

      if (user.id !== inquiry.user.id) {
        return {
          props: {
            user,
            type: 'error' as const,
            error: 'error-wrong-user' as const,
            inquiryId,
            ...await translations,
          },
        };
      }

      const broadcastBoats = await serverGetBroadcastBoats(context, inquiry);

      if (broadcastBoats.length === 0) {
        return {
          props: {
            user,
            type: 'error' as const,
            error: 'error-broadcast-empty' as const,
            inquiryId,
            ...await translations,
          },
        };
      }

      return {
        props: {
          user,
          type: 'success' as const,
          broadcastBoats,
          inquiryId,
          tripId: inquiry.thread_id,
          listing,
          ...await translations,
        },
      };
    },
  ),
);

export default BroadcastPage;
